import { Application, BasePlugin } from '@goodboydigital/astro';

import { App } from '../App';
import { Settings } from '../AppConfig';
import { VehicleData } from '../game/entities/VehicleEntity';
import { deepAssign, deepClone } from '../utils/DSUtils';

/**
 * Default user data fo local stoage pesistence.
 * Nickname and flag has been moved to vehicle data to make it simpler to
 * assemble vehicles also to push all these things to replay data
 * */
const defaultUserData = {
    /** Persistent data version - changing this will reset all local storage data */
    dataVersion: 1,

    /** Number of coins that user has */
    coins: 0,

    /** Number of diamonds that user has */
    diamonds: 0,

    /** Current race number to be played */
    race: 0,

    /** Current player rank points */
    rank: 0,

    /** User position per race played - `racePositions['3'] = 5` means race #3 user got the 5th position */
    racePositions: {} as Record<number, number>,

    /** Num players in each race played - `racePlayers['3'] = 5` means race #3 had 5 players, counting user */
    racePlayers: {} as Record<number, number>,

    /** best record on each race, in miliseconds - `raceTimes['3'] = 20000` means race #3 best time is 2 seconds */
    raceTimes: {} as Record<number, number>,

    /** Best times per level hash. Might be changed in favour of the new ordered race system */
    bestRuns: {} as Record<string, number>,

    /** User vehicle setup - nickname also included because it is easier to pack altogether to replay data */
    vehicle: {
        nickname: '',
        flag: '',
        chassis: Settings.browserLite ? 'beachcomber' : '',
        character: 'gumball',
        wheelLeft: '',
        wheelRight: '',
        trailLeft: '',
        trailRight: '',
        colorChassis: Settings.browserLite ? '#1a92a1' : '',
        colorWheelLeft: Settings.browserLite ? '#ffffff' : '',
        colorWheelRight: Settings.browserLite ? '#ffffff' : '',
        extra: '',
    } as VehicleData,

    /** General app settings */
    settings: {
        mute: false,
        language: '',
        languageChanged: false,
        termsAccepted: false,
        hasPlayerSetName: false,
    },

    /** All content that has been unlocked/purchased by the user */
    purchasedContent: {
        bike: {},
        character: {},
        wheel: {},
        trail: {},
        deco: {},
        color: {},
    } as Record<'bike'|'character'|'wheel'|'trail'|'deco'|'color', Record<string, string>>,
};

/** User data type, based in the default object */
export type UserData = typeof defaultUserData;

/**
 * Handles user data. Data loaded is from local storage when app starts, however changes in data
 * does not get saved automatically. So if data changes, let's say, number of coins, the save()
 * function must be manually called after that, otherwise the change will not persist
 */
export class UserPlugin implements BasePlugin
{
    public app: App;
    public data!: UserData;

    constructor(app: Application)
    {
        this.app = app as App;
        this.load();

        if (Settings.reset) this.resetData();
    }

    /** Load current data from local storage */
    public load(): void
    {
        this.data = deepClone(defaultUserData);

        const storedData = (this.app.storage.getObject('user') || {}) as Partial<UserData>;

        // Ignore all stored data if version does not match.
        // Can be replaced by a migration function if data format has been changed.
        if (storedData.dataVersion === this.data.dataVersion)
        {
            deepAssign(this.data, storedData);
        }

        if (Settings.rank >= 0) this.data.rank = Settings.rank;
        if (Settings.coins >= 0) this.data.coins = Settings.coins;
        if (Settings.diamonds >= 0) this.data.diamonds = Settings.diamonds;
        if (Settings.browserLite) this.data.vehicle.character = 'gumball';

        this.save();
    }

    /** Save current data into local storage */
    public save(): void
    {
        this.app.storage.setItem('user', this.data);
    }

    /** Reset persistent data to fatcory settings, expect language & terms agreement */
    public resetData(): void
    {
        const data = deepClone(defaultUserData);

        this.data = data;
        this.save();
    }

    /** Delete all locally stored runs */
    public deleteBestRuns(): void
    {
        this.data.bestRuns = {};
        this.save();
    }

    /** Delete all locally stored data */
    public deleteUserData(): void
    {
        this.data = deepClone(defaultUserData);
        this.save();
    }

    public getCoins(): number
    {
        return this.data.coins;
    }

    public changeCoins(num: number): void
    {
        this.data.coins += Math.round(num);
        if (this.data.coins < 0) this.data.coins = 0;
        this.save();
    }

    public getDiamonds(): number
    {
        return this.data.diamonds;
    }

    public changeDiamonds(num: number): void
    {
        this.data.diamonds += Math.round(num);
        if (this.data.diamonds < 0) this.data.diamonds = 0;
        this.save();
    }

    public setRace(num: number): void
    {
        this.data.race = Math.round(num);
        if (this.data.race < 0) this.data.race = 0;
        this.save();
    }

    public nextRace(): void
    {
        this.setRace(Math.max(this.data.race, 0) + 1);
    }

    public prevRace(): void
    {
        this.setRace(Math.max(this.data.race, 0) - 1);
    }

    public setRaceResults(raceNum: number, position: number, players: number, time: number): void
    {
        // Skip tutorial race
        if (raceNum === 0) return;

        const bestPosition = !this.data.racePositions[raceNum] || this.data.racePositions[raceNum] > position;
        const bestTime = !this.data.raceTimes[raceNum] || this.data.raceTimes[raceNum] > time;

        // Save position and number of players only is position is better than before
        if (bestPosition)
        {
            this.data.racePositions[raceNum] = position;
            this.data.racePlayers[raceNum] = players;
            this.app.rank.increaseRank(position, players);
        }
        else
        {
            this.app.rank.lastPointsEarned = 0;
        }

        // Save time only if is better than before - might not correspond to the same race as the best position
        if (bestTime)
        {
            console.log(time)
            this.data.raceTimes[raceNum] = time;
        }

        this.save();
    }

    public getSavedPositionByRace(index: number): number
    {
        if (isNaN(this.data.racePositions[index])) return 0;

        return this.data.racePositions[index];
    }

    public getSavedPlayersByRace(index: number): number
    {
        // 6 is a temporary fallback number for races that hadn't registered num of players
        // Number of players can vary according to number of ghost data available per race and
        // how much the client can load before the race starts
        if (isNaN(this.data.racePlayers[index])) return 6;

        return this.data.racePlayers[index] || 6;
    }

    public getSavedTimeByRace(index: number): number
    {
        const fallbackTime = 1000 * 60 * 2; // 2 minutes in miliseconds
        
        return this.data.raceTimes[index] || fallbackTime;
    }

    public numRacesPlayed(): number
    {
        return Object.keys(this.data.racePositions).length;
    }

    public onTutorial(): boolean
    {
        return this.data.race <= 0;
    }

    public getNickname(): string
    {
        return this.data.vehicle.nickname;
    }

    public getFlag(): string
    {
        return this.data.vehicle.flag;
    }

    public getRacesPlayed(): number
    {
        return Object.keys(this.data.racePositions).length;
    }

    public getRacesWon(): number
    {
        let i = 0;

        for (const race in this.data.racePositions)
        {
            const pos = this.data.racePositions[race];

            if (pos === 1) i++;
        }

        return i;
    }

    public getRankPoints(): number
    {
        return this.data.rank;
    }

    public setRankPoints(points: number, autoSave = true): void
    {
        if (this.data.rank === points) return;
        this.data.rank = points;
        if (this.data.rank < 0) this.data.rank = 0;
        if (autoSave) this.save();
    }

    public update(): void
    {
        //
    }
}
